import jwt_decode from 'jwt-decode';
import { getUTMFromStorage } from '../marketing/utm/utm-provider';
import { externalLogin, ILoginReferrer } from '../../projects/membership/dal/data-provider';
import { ExternalLoginServiceEnum } from '../../projects/membership/enums/external-service-enum';
import { getPageNodeGUID } from '../common';
import { redirectAfterLogin } from '../../projects/membership/login/standard-login/login';
import { IWavesAppDetection } from '../../projects/membership/login/waves-app-login/interfaces';
import { ILocalDataRequest } from '../../projects/shopping-cart/cart/interfaces';

export const GOOGLE_SIGN_IN_INITIALIZED = 'GOOGLE_SIGN_IN_INITIALIZED';
export const GOOGLE_SIGNED_IN = 'GOOGLE_SIGNED_IN';
export const GOOGLE_ERROR = 'GOOGLE_ERROR';

export interface IGoogleJwt {
    aud: string, // Audience
    azp: string, // Authorized party - the party to which the ID Token was issued.
    email: string,
    email_verified: boolean,
    exp: number,
    family_name: string, // last name
    given_name: string, // first name
    name: string, // full name
    iat: number, //  (Issued At) Claim The "iat" (issued at) claim identifies the time at which the JWT was issued.
    iss: string, // https://accounts.google.com
    jti: string, //  (JWT ID) claim is a unique identifier for the JWT. This can be used to prevent JWT token abuse, and to ensure the uniqueness of the JWT. The "jti" claim is an optional claim, but it is recommended that it be included in the JWT if it is available.
    locale: string, // en-GB
    nbf: string, //  (Not Before) Claim The "nbf" (not before) claim identifies the time before which the JWT MUST NOT be accepted for processing. The processing of the "nbf" claim requires that the current date/time MUST be after or equal to the not-before date/time listed in the "nbf" claim.
    picture: string, // url of user avatar
    sub: string, //  (Subject) Claim The "sub" (subject) claim identifies the principal that is the subject of the JWT.This can be used as user ID.
}

/**
 * https://developers.google.com/identity/gsi/web/reference/js-reference
 */
export const initGoogleForLogin = (isWavesApp: boolean, wavesAppDetectionData?: IWavesAppDetection, isShoppingCart?: boolean, localData?: ILocalDataRequest) => {

    /**
     * With Popup mode, you add a custom HTTP header to your requests,
     * and then on your server confirm it matches the expected value and origin.
     */
    const handleCredentialResponse = async (response: google.accounts.id.CredentialResponse) => {

        if(!response) return;

        try{
            const decoded = jwt_decode(response.credential) as IGoogleJwt;

            const isPersistent = true;

            const utm = getUTMFromStorage();

            const serverResponse = await externalLogin(
                ExternalLoginServiceEnum.Google,
                response.credential,
                decoded.sub,
                decoded.email,
                decoded.name,
                isShoppingCart ? ILoginReferrer.CartExternalLogin : ILoginReferrer.MainExternalLogin,
                isPersistent,
                !!isShoppingCart,
                localData,

                wavesAppDetectionData?.appCode,
                wavesAppDetectionData?.ts || 0,
                wavesAppDetectionData?.uuid,
                wavesAppDetectionData?.qs,

                // message gears -------
                getPageNodeGUID(),
                utm ? utm.firstSource : '',
                utm ? utm.firstMedium : '',
                utm ? utm.firstCampaign : '',
                utm ? utm.lastSource : '',
                utm ? utm.lastMedium : '',
                utm ? utm.lastCampaign : '',
            );

            if(
                !serverResponse ||
                serverResponse.statusCode !== 200 ||
                !serverResponse.data.isValid){
                document.body.dispatchEvent(new CustomEvent(GOOGLE_ERROR, {
                    detail: serverResponse
                }));
                return;
            }

            // All is well -----------------
            if(isWavesApp || isShoppingCart) {
                document.body.dispatchEvent(new CustomEvent(GOOGLE_SIGNED_IN, {
                    detail: serverResponse,
                }))
            }
            else{
                redirectAfterLogin();
            }
        }
        catch (ex) {
            console.log(ex);
        }
    };

    window.onload = () => {

        // The google.accounts.id.initialize method should be called only once,
        // even if you use both One Tap and button in the same web page.
        google.accounts.id.initialize({
            client_id: '429317021265-opt92t3o43q0rh84g6mans6bs4747lrk.apps.googleusercontent.com',
            callback: handleCredentialResponse,
        });

        if(!isShoppingCart) {
            document.body.dispatchEvent(new CustomEvent(GOOGLE_SIGN_IN_INITIALIZED));
        }
    };
};

/**
 * https://developers.google.com/identity/gsi/web/reference/js-reference#google.accounts.id.disableAutoSelect
 * When the user signs out of your website, you need to call the method google.accounts.id.disableAutoSelect
 * to record the status in cookies. This prevents a UX dead loop.
 */
export const googleSignOut = () => {
    if(!window.google || !window.google.accounts || !window.google.accounts.id) return;
    window.google.accounts.id.disableAutoSelect();
};
